import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FiColumns, FiTag } from "react-icons/fi";
import { toast } from "react-toastify";
import useCallCenterT from "../../hooks/useCallCenterT";
import { hasScreenAccess } from "../../utils/permissions";
import TicketColumnConfigPanel from "../../components/call-center/TicketColumnConfigPanel";
import TicketStatusSettingsPanel from "../../components/call-center/TicketStatusSettingsPanel";
import TableSkeleton from "../../components/call-center/listing/TableSkeleton";
import {
  LISTING_CARD_CLASS,
  LISTING_CONTAINER_CLASS,
  LISTING_PAGE_CLASS,
  LISTING_PRIMARY_BUTTON_CLASS,
} from "../../components/call-center/listing/listingStyles";
import { getTicketColumnConfig, updateTicketColumnConfig } from "../../service/CallCenterTickets";

const TAB_COLUMNS = "columns";
const TAB_STATUSES = "statuses";

const CallCenterTicketSettingsPage = () => {
  const t = useCallCenterT();
  const [searchParams, setSearchParams] = useSearchParams();
  const saveRef = useRef(null);

  const canColumns = hasScreenAccess("CC_TICKET_COLUMNS", "canView");
  const canStatuses = hasScreenAccess("CC_SETTINGS", "canView");
  const tabs = useMemo(
    () =>
      [
        canColumns
          ? { id: TAB_COLUMNS, label: t("ccTicketSettingsTabColumns"), icon: FiColumns }
          : null,
        canStatuses
          ? { id: TAB_STATUSES, label: t("ccTicketSettingsTabStatuses"), icon: FiTag }
          : null,
      ].filter(Boolean),
    [canColumns, canStatuses, t]
  );

  const defaultTab = tabs[0]?.id || TAB_COLUMNS;
  const requestedTab = searchParams.get("tab");
  const activeTab = tabs.some((tab) => tab.id === requestedTab) ? requestedTab : defaultTab;

  useEffect(() => {
    if (!tabs.length) return;
    if (requestedTab !== activeTab) {
      setSearchParams({ tab: activeTab }, { replace: true });
    }
  }, [activeTab, requestedTab, setSearchParams, tabs.length]);

  const [columns, setColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);
  const [columnsSaving, setColumnsSaving] = useState(false);
  const [columnsError, setColumnsError] = useState("");

  const loadColumns = useCallback(async () => {
    if (!canColumns) return;
    setColumnsLoading(true);
    setColumnsError("");
    try {
      const result = await getTicketColumnConfig();
      setColumns(Array.isArray(result) ? result : []);
    } catch (error) {
      const message = error.message || t("ccToastLoadColumnConfigFailed");
      setColumnsError(message);
      toast.error(message);
      setColumns([]);
    } finally {
      setColumnsLoading(false);
    }
  }, [canColumns, t]);

  useEffect(() => {
    if (activeTab === TAB_COLUMNS && canColumns) {
      loadColumns();
    }
  }, [activeTab, canColumns, loadColumns]);

  const handleSaveColumnConfig = async (nextColumns) => {
    try {
      setColumnsSaving(true);
      const saved = await updateTicketColumnConfig({ columns: nextColumns });
      setColumns(Array.isArray(saved) ? saved : []);
      toast.success(t("ccToastColumnConfigSaved"));
    } catch (error) {
      toast.error(error.message || t("ccToastColumnConfigSaveFailed"));
    } finally {
      setColumnsSaving(false);
    }
  };

  const setActiveTab = (tabId) => {
    setSearchParams({ tab: tabId }, { replace: true });
  };

  if (!tabs.length) {
    return (
      <div className={LISTING_PAGE_CLASS}>
        <div className={`${LISTING_CONTAINER_CLASS} max-w-xl`}>
          <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900">
            {t("ccTicketSettingsNoTabAccess")}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={LISTING_PAGE_CLASS}>
      <div className={LISTING_CONTAINER_CLASS}>
        <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-[28px] font-bold tracking-tight text-[#111827]">{t("ccTicketSettingsPageTitle")}</h1>
            <p className="mt-1 text-sm text-[#6b7280]">{t("ccTicketSettingsPageSubtitle")}</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {activeTab === TAB_COLUMNS && canColumns ? (
              <button
                type="button"
                onClick={() => saveRef.current?.()}
                disabled={columnsSaving || columnsLoading}
                className={LISTING_PRIMARY_BUTTON_CLASS}
              >
                {columnsSaving ? t("ccSaving") : t("ccSaveColumnConfig")}
              </button>
            ) : null}
          </div>
        </header>

        {tabs.length > 1 ? (
          <div className="mb-6 inline-flex rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const selected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold transition ${
                    selected
                      ? "bg-[#2B7FFF] text-white shadow-sm"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        ) : null}

        {activeTab === TAB_COLUMNS && canColumns ? (
          <section>
            {columnsError ? (
              <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                <span>{columnsError}</span>
                <button type="button" onClick={loadColumns} className="font-semibold hover:underline">
                  {t("ccRefresh")}
                </button>
              </div>
            ) : null}

            {columnsLoading ? (
              <div className={`${LISTING_CARD_CLASS} p-4`}>
                <TableSkeleton columns={7} rows={8} />
              </div>
            ) : (
              <TicketColumnConfigPanel
                columns={columns}
                onSave={handleSaveColumnConfig}
                saving={columnsSaving}
                saveControlRef={saveRef}
              />
            )}
          </section>
        ) : null}

        {activeTab === TAB_STATUSES && canStatuses ? <TicketStatusSettingsPanel /> : null}
      </div>
    </div>
  );
};

export default CallCenterTicketSettingsPage;
